import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, ClipboardList, PlayCircle, CheckCircle2, XCircle, TrendingUp } from "lucide-react";

const PRIORITIES = ["low", "medium", "high", "urgent"];
const STATUSES = ["planned", "in_progress", "completed", "cancelled"];

const prioColor = (p) =>
  p === "urgent" ? "bg-red-100 text-red-800" :
  p === "high" ? "bg-amber-100 text-amber-800" :
  p === "medium" ? "bg-slate-100 text-slate-700" : "bg-emerald-50 text-emerald-700";

const statusColor = (s) =>
  s === "completed" ? "bg-emerald-100 text-emerald-800" :
  s === "in_progress" ? "bg-blue-100 text-blue-800" :
  s === "cancelled" ? "bg-red-100 text-red-800" : "bg-slate-100 text-slate-700";

export function MfgPlansTab() {
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [lines, setLines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().slice(0, 10));
  const [branchFilter, setBranchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const loadRefs = async () => {
    try {
      const [b, p] = await Promise.all([api.get("/branches"), api.get("/products")]);
      setBranches(b.data);
      setProducts(p.data);
    } catch { /* */ }
  };
  useEffect(() => { loadRefs(); }, []);

  const load = async () => {
    const params = new URLSearchParams();
    if (dateFilter) params.set("plan_date", dateFilter);
    if (branchFilter) params.set("branch_id", branchFilter);
    if (statusFilter) params.set("status", statusFilter);
    try {
      const q = params.toString() ? `?${params.toString()}` : "";
      const [r, s] = await Promise.all([
        api.get(`/mfg/plans${q}`),
        api.get(`/mfg/plans/summary${dateFilter ? `?plan_date=${dateFilter}${branchFilter ? `&branch_id=${branchFilter}` : ""}` : ""}`),
      ]);
      setRows(r.data);
      setSummary(s.data);
    } catch { /* */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFilter, branchFilter, statusFilter]);

  // Load lines/shifts scoped to selected factory in form
  useEffect(() => {
    const bid = form.branch_id;
    if (!bid) { setLines([]); setShifts([]); return; }
    Promise.all([
      api.get(`/mfg/lines?branch_id=${bid}`),
      api.get(`/mfg/shifts?branch_id=${bid}`),
    ]).then(([l, s]) => { setLines(l.data); setShifts(s.data); }).catch(() => {});
  }, [form.branch_id]);

  const branchName = (id) => (branches.find((b) => b.id === id) || {}).name || id;

  const save = async () => {
    if (!form.branch_id || !form.product_id || !form.plan_date || !form.target_qty) {
      toast.error("Factory, product, date and target qty required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        target_qty: Number(form.target_qty),
        target_cases: form.target_cases ? Number(form.target_cases) : undefined,
        target_cartons: form.target_cartons ? Number(form.target_cartons) : undefined,
      };
      if (form.id) {
        // PATCH doesn't accept plan_date/branch_id/product_id
        const { id, branch_id, product_id, plan_date, recipe_id, product_name, product_sku, completion_pct, created_at, created_by, updated_at, ...body } = payload;
        await api.patch(`/mfg/plans/${id}`, body);
      } else {
        await api.post("/mfg/plans", payload);
      }
      toast.success("Saved");
      setOpen(false);
      setForm({});
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const patchStatus = async (row, newStatus, extra = {}) => {
    try {
      await api.patch(`/mfg/plans/${row.id}`, { status: newStatus, ...extra });
      toast.success(`Marked ${newStatus.replace("_", " ")}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete plan?")) return;
    try { await api.delete(`/mfg/plans/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openNew = () => {
    setForm({ plan_date: dateFilter, priority: "medium", status: "planned", branch_id: branchFilter || undefined });
    setOpen(true);
  };

  return (
    <div className="pt-3 space-y-4" data-testid="mfg-plans-tab">
      {/* Summary tiles */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <SummaryTile label="Planned" count={summary.buckets?.planned?.count} target={summary.buckets?.planned?.target} tone="slate" />
          <SummaryTile label="In Progress" count={summary.buckets?.in_progress?.count} target={summary.buckets?.in_progress?.target} actual={summary.buckets?.in_progress?.actual} tone="blue" />
          <SummaryTile label="Completed" count={summary.buckets?.completed?.count} target={summary.buckets?.completed?.target} actual={summary.buckets?.completed?.actual} tone="emerald" />
          <SummaryTile label="Cancelled" count={summary.buckets?.cancelled?.count} tone="red" />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div>
          <Label className="text-[10px] uppercase text-[#8B7355]">Date</Label>
          <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="w-40" data-testid="mfg-plans-date-filter" />
        </div>
        <div>
          <Label className="text-[10px] uppercase text-[#8B7355]">Factory</Label>
          <Select value={branchFilter} onValueChange={(v) => setBranchFilter(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-56" data-testid="mfg-plans-branch-filter"><SelectValue placeholder="All factories" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="__all">All factories</SelectItem>
              {branches.filter((b) => b.type === "factory").map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-[#8B7355]">Status</Label>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-44" data-testid="mfg-plans-status-filter"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="__all">Any status</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" className="bg-[#C05621] text-white ml-auto self-end" onClick={openNew} data-testid="mfg-plan-new">
          <Plus className="w-3.5 h-3.5 mr-1" /> New Plan
        </Button>
      </div>

      {/* Table */}
      <div className="border border-[#EAE5D9] rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="mfg-plans-rows">
          <thead className="bg-[#FAF6EE] text-[#8B7355] text-[11px] uppercase">
            <tr>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Factory</th>
              <th className="text-left px-3 py-2">Product</th>
              <th className="text-right px-3 py-2">Target</th>
              <th className="text-right px-3 py-2">Actual</th>
              <th className="text-right px-3 py-2">%</th>
              <th className="text-center px-3 py-2">Priority</th>
              <th className="text-center px-3 py-2">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#EAE5D9] hover:bg-[#FDFBF7]" data-testid={`mfg-plan-row-${r.id}`}>
                <td className="px-3 py-2 text-xs whitespace-nowrap">{r.plan_date}</td>
                <td className="px-3 py-2 text-xs">{branchName(r.branch_id)}</td>
                <td className="px-3 py-2"><div className="font-medium">{r.product_name}</div><div className="text-[10px] text-[#8B7355]">{r.product_sku}</div></td>
                <td className="px-3 py-2 text-right font-mono">{r.target_qty}</td>
                <td className="px-3 py-2 text-right font-mono">{r.actual_qty ?? 0}</td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <span className="text-xs">{r.completion_pct ?? 0}%</span>
                    <div className="w-16 h-1.5 bg-slate-100 rounded overflow-hidden hidden sm:block">
                      <div className={`h-full ${(r.completion_pct ?? 0) >= 100 ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${Math.min(100, r.completion_pct || 0)}%` }} />
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2 text-center"><Badge className={`border-0 ${prioColor(r.priority)}`}>{r.priority}</Badge></td>
                <td className="px-3 py-2 text-center"><Badge className={`border-0 ${statusColor(r.status)}`}>{r.status?.replace("_", " ")}</Badge></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {r.status === "planned" && (
                    <Button size="sm" variant="outline" className="text-blue-700 border-blue-300" onClick={() => patchStatus(r, "in_progress")} data-testid={`mfg-plan-start-${r.id}`}>
                      <PlayCircle className="w-3 h-3 mr-1" /> Start
                    </Button>
                  )}
                  {r.status === "in_progress" && (
                    <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300 ml-1" onClick={async () => {
                      const q = window.prompt("Actual qty produced?", r.actual_qty || r.target_qty);
                      if (q === null) return;
                      patchStatus(r, "completed", { actual_qty: Number(q) });
                    }} data-testid={`mfg-plan-complete-${r.id}`}>
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Complete
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="ml-1" onClick={() => { setForm(r); setOpen(true); }} data-testid={`mfg-plan-edit-${r.id}`}>Edit</Button>
                  {r.status !== "cancelled" && r.status !== "completed" && (
                    <Button size="sm" variant="ghost" className="text-red-600 ml-1" onClick={() => patchStatus(r, "cancelled")} title="Cancel" data-testid={`mfg-plan-cancel-${r.id}`}>
                      <XCircle className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="text-red-600 ml-1" onClick={() => del(r.id)}>×</Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={9} className="text-center py-12 text-[#8B7355]">
                <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No production plans for {dateFilter}. Click <b>New Plan</b> to schedule one.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create/edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader><DialogTitle className="font-[Outfit]">{form.id ? "Edit" : "New"} Production Plan</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div><Label>Plan date*</Label>
              <Input type="date" value={form.plan_date || ""} disabled={!!form.id} onChange={(e) => setForm({ ...form, plan_date: e.target.value })} data-testid="mfg-plan-form-date" />
            </div>
            <div><Label>Factory*</Label>
              <Select value={form.branch_id || ""} onValueChange={(v) => setForm({ ...form, branch_id: v, line_id: "", shift_id: "" })} disabled={!!form.id}>
                <SelectTrigger data-testid="mfg-plan-form-branch"><SelectValue placeholder="Factory" /></SelectTrigger>
                <SelectContent className="bg-white">{branches.filter((b) => b.type === "factory").map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Product*</Label>
              <Select value={form.product_id || ""} onValueChange={(v) => setForm({ ...form, product_id: v })} disabled={!!form.id}>
                <SelectTrigger data-testid="mfg-plan-form-product"><SelectValue placeholder="Choose product" /></SelectTrigger>
                <SelectContent className="bg-white max-h-72 overflow-y-auto">{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.sku})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Line</Label>
              <Select value={form.line_id || ""} onValueChange={(v) => setForm({ ...form, line_id: v === "__none" ? "" : v })}>
                <SelectTrigger data-testid="mfg-plan-form-line"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="__none">Unassigned</SelectItem>{lines.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Shift</Label>
              <Select value={form.shift_id || ""} onValueChange={(v) => setForm({ ...form, shift_id: v === "__none" ? "" : v })}>
                <SelectTrigger data-testid="mfg-plan-form-shift"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="__none">Unassigned</SelectItem>{shifts.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.start_time}-{s.end_time})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Target qty*</Label>
              <Input type="number" step="0.1" min="0" value={form.target_qty || ""} onChange={(e) => setForm({ ...form, target_qty: e.target.value })} data-testid="mfg-plan-form-target" />
            </div>
            <div><Label>Target cases</Label>
              <Input type="number" step="0.1" min="0" value={form.target_cases || ""} onChange={(e) => setForm({ ...form, target_cases: e.target.value })} />
            </div>
            <div><Label>Planned start</Label>
              <Input type="datetime-local" value={form.planned_start || ""} onChange={(e) => setForm({ ...form, planned_start: e.target.value })} />
            </div>
            <div><Label>Planned end</Label>
              <Input type="datetime-local" value={form.planned_end || ""} onChange={(e) => setForm({ ...form, planned_end: e.target.value })} />
            </div>
            <div><Label>Priority</Label>
              <Select value={form.priority || "medium"} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="mfg-plan-form-priority"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status || "planned"} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.id && (
              <div><Label>Actual qty produced</Label>
                <Input type="number" step="0.1" min="0" value={form.actual_qty ?? ""} onChange={(e) => setForm({ ...form, actual_qty: e.target.value ? Number(e.target.value) : 0 })} data-testid="mfg-plan-form-actual" />
              </div>
            )}
            <div className="col-span-2"><Label>Notes</Label>
              <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#2C1810] text-white" data-testid="mfg-plan-form-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryTile({ label, count, target, actual, tone }) {
  const bg = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    red: "bg-red-50 border-red-200 text-red-700",
  }[tone] || "bg-white border-[#EAE5D9]";
  return (
    <div className={`border rounded-md p-3 ${bg}`} data-testid={`plan-tile-${label.toLowerCase().replace(" ", "-")}`}>
      <div className="text-[10px] uppercase tracking-wider flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {label}</div>
      <div className="text-2xl font-[Outfit] font-semibold mt-0.5">{count ?? 0}</div>
      <div className="text-[10px] mt-0.5 opacity-80">
        {target !== undefined && <>target: <b>{Math.round(target || 0)}</b></>}
        {actual !== undefined && <> · actual: <b>{Math.round(actual || 0)}</b></>}
      </div>
    </div>
  );
}
